import { fetchPricesWithUuidApi, useFreeEstimationApi } from "../../../api/TarifsApi";
import Download from "../../../components/atoms/icons/general/download/Download";
import FilledButton from "../../../components/molecules/buttons/filledButton/FilledButton";
import UnfilledButton from "../../../components/molecules/buttons/unfilledButton/UnfilledButton";
import AppContext from "../../../context/AppContext";
import { useAuth } from "../../../context/AuthContext";
import RapportFDCPicture from "../../../assets/reportExamples/exemple_rapport_fdc.webp";
import RapportMCPicture from "../../../assets/reportExamples/exemple_rapport_mc.webp";
import RapportTDSPicture from "../../../assets/reportExamples/exemple_rapport_tds.webp";
import "./Tarifs.css";
import { useContext, useEffect, useState } from "react";
import { createDownloadLink } from "../../../utils/Utils";
import RapportFDC from "../../../assets/reportExamples/exemple_rapport_fdc.pdf";
import RapportMC from "../../../assets/reportExamples/exemple_rapport_mc.pdf";
import RapportTDS from "../../../assets/reportExamples/exemple_rapport_tds.pdf";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../../../context/CartContext";
import { useModals } from "../../../context/ModalsContext";
import Loader from "../../../components/organisms/loader/Loader";
import UseFreeEstimationModal from "../../../components/forms/modals/tarifs/useFreeEstimationModal/UseFreeEstimationModal";

function Tarifs() {
  const { createNotification, setBannerConfig, setModalContent, setModalVisible } = useContext(AppContext);
  const { getUuid, subscription } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [cartLink, setCartLink] = useState("/panier");
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { showRegisterModal } = useModals();

  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [tarifsData, setTarifsData] = useState([]);
  const [activeRapport, setActiveRapport] = useState(1);
  const [isFreeEstimationVisible, setIsFreeEstimationVisible] = useState(false);

  const [userEstimations, setUserEstimations] = useState([]);
  const [isEstimationsLoading, setIsEstimationsLoading] = useState(true);
  const [selectedEstimation, setSelectedEstimation] = useState(null);
  const [selectedEstimationsUuids, setSelectedEstimationsUuids] = useState([]);

  useEffect(() => {
    fetchPrices();
    setBannerConfig({
      title: "Nos offres",
    });
  }, []);

  useEffect(() => {
    const fallbackUrl = searchParams.get("fallback-url");

    if (fallbackUrl) setCartLink("/panier?fallback-url=" + fallbackUrl);
    else setCartLink("/panier");
  }, [searchParams]);

  function getPrettyPrice(price) {
    let result = price === Math.round(price) ? price : price?.toFixed(2);
    return (result + "").replace(".", ",");
  }

  function pickTarif(tarif) {
    if (!getUuid()) return showRegisterModal(" pour profiter de nos offres", " pour profiter de nos offres");

    const tarifForCart = { ...tarif };

    if (tarifForCart.original_base_price !== undefined) {
      tarifForCart.base_price_by_month = tarifForCart.original_base_price;
    }

    addToCart(tarifForCart);
    navigate(cartLink);
    return;
  }

  async function fetchPrices() {
    setIsLoaderVisible(true);
    try {
      let res = (await fetchPricesWithUuidApi(getUuid())).data;

      setIsFreeEstimationVisible(res.has_free_estimation);

      res = res.offers.filter(tarif => tarif.is_displayed);

      const defaultPrice = res.reduce((max, tarif) => Math.max(tarif.base_price_by_month, max), 0);
      const finalTarifs = res.map(tarif => formatTarif(tarif, defaultPrice));

      setTarifsData(finalTarifs);
    } catch (error) {
      createNotification(
        <>Une erreur est survenue lors de la récupération des tarifs. Veuillez réessayer.</>,
        "var(--red)",
        "var(--dark-blue)",
      );
    } finally {
      setIsLoaderVisible(false);
    }
  }

  function formatTarif(tarif, defaultPrice) {
    const res = tarif;
    res.intro_text = getCommitmentDescription(tarif);
    res.toggle_text = getToggleText(tarif);
    res.class = getClass(tarif);
    res.default_price = defaultPrice;
    res.default_discount = getDefaultDiscount(tarif);
    res.discount_value = getDiscountValue(tarif);
    res.final_price = getFinalPrice(tarif);
    res.commitment = getCommitment(tarif);

    return res;
  }

  function getClass(tarif) {
    switch (tarif.price_name) {
      case "Liberty":
        return "liberty";
      case "Maestro":
        return "maestro";
      case "Optimum":
        return "optimum";
      default:
        return "";
    }
  }

  function getCommitment(tarif) {
    return tarif.is_commitment ? `Engagement sur ${tarif.commitment_duration_in_months} mois` : "Sans engagement";
  }

  function getFinalPrice(tarif) {
    if (tarif.discount?.discounted_price_by_month) {
      return tarif.discount.discounted_price_by_month;
    } else if (tarif.default_discount) {
      return tarif.base_price_by_month * (1 - tarif.default_discount / 100);
    }
    return tarif.base_price_by_month;
  }

  function getToggleText(tarif) {
    if (!tarif.is_commitment && tarif.duration_in_months === 1)
      return (
        <>
          Mensuel <br />
          sans engagement
        </>
      );

    if (tarif.is_commitment && tarif.duration_in_months === 1 && tarif.commitment_duration_in_months === 12)
      return (
        <>
          12 mois <br />
          paiement mensuel
        </>
      );

    if (tarif.is_commitment && tarif.commitment_duration_in_months === 12 && tarif.duration_in_months === 12)
      return (
        <>
          12 mois <br />
          paiement annuel
        </>
      );

    return <></>;
  }

  function getDefaultDiscount(tarif) {
    if (!tarif.is_commitment) return 0;

    if (tarif.is_commitment && tarif.commitment_duration_in_months === 12 && tarif.duration_in_months === 1) return 20;

    if (tarif.is_commitment && tarif.commitment_duration_in_months === 12 && tarif.duration_in_months === 12) return 33;

    return 0;
  }

  function getCommitmentDescription(tarif) {
    if (!tarif.is_commitment)
      return (
        <>
          Pour le prix d'une seule estimation <br /> vous bénéficiez d'un mois entier d'utilisation
        </>
      );

    if (tarif.is_commitment && tarif.commitment_duration_in_months === 12 && tarif.duration_in_months === 1)
      return <>Pour une utilisation annuelle avec un paiement mensuel</>;

    if (tarif.is_commitment && tarif.commitment_duration_in_months === 12 && tarif.duration_in_months === 12)
      return <>Pour une utilisation annuelle avec un paiement en une fois</>;

    return <></>;
  }

  function getDiscountValue(tarif) {
    return tarif.discount?.value ?? 0;
  }

  function getFinalPrice(tarif) {
    return tarif.discount?.discounted_price_by_month ?? tarif.base_price_by_month;
  }

  function hasDiscount(tarif) {
    return tarif?.default_discount || tarif?.discount?.discount_value;
  }

  function hasBothDiscount(tarif) {
    return !!(tarif?.default_discount && tarif?.discount?.discount_value);
  }

  function downloadRapportExample(activeRapport) {
    let uri;
    let fileName = "exemple_rapport_";

    switch (activeRapport) {
      case 0:
        uri = RapportMC;
        fileName += "murs_commerciaux";
        break;
      case 1:
        uri = RapportFDC;
        fileName += "fonds_de_commerce";
        break;
      default:
        uri = RapportTDS;
        fileName += "titres_de_societe";
    }
    const link = createDownloadLink(uri);
    if (!link)
      return createNotification(
        <>Impossible de télécharger l'exemple de rapport. Veuillez réessayer.</>,
        "var(--red)",
        "var(--dark-blue)",
      );

    link.download = fileName;
    link.click();
    link.remove();
  }

  async function useFreeEstimation() {
    if (isFreeEstimationVisible && getUuid())
      try {
        await useFreeEstimationApi(getUuid());
        setIsFreeEstimationVisible(false);

        setModalContent({
          title: "Estimation offerte",
          content: <UseFreeEstimationModal />,
          className: "medium-modal",
        });

        setModalVisible(true);
      } catch (error) {
        createNotification(
          <>Une erreur est survenue lors de la demande d'estimation offerte. Veuillez réessayer.</>,
          "var(--red)",
          "var(--dark-blue)",
        );
      }
  }

  function isTarifDisabled(tarif) {
    return subscription?.is_commitment || subscription?.plan === tarif?.price_name;
  }

  return (
    <div className='container'>
      <div className='tarifs-page-container '>
        <div className='w-100 relative'>
          <div className='tarifs-toggle-container'>
            {tarifsData.map((tarif, index) => (
              <div
                key={tarif.price_id}
                className={`tarifs-toggle ${activeTab === index ? "active" : ""}`}
                onClick={() => setActiveTab(index)}>
                {tarif.toggle_text}
                {tarif?.default_discount ? (
                  <div className='tarifs-toggle-discount'>{getPrettyPrice(tarif.default_discount)}% d'économie</div>
                ) : null}
              </div>
            ))}
          </div>
          <p className='tarifs-intro-text'>{tarifsData[activeTab]?.intro_text}</p>
          <div className={`tarifs-card ${tarifsData[activeTab]?.class}`}>
            <div className='tarifs-title'>{tarifsData[activeTab]?.price_name}</div>
            <div className='tarifs-content'>
              {hasDiscount(tarifsData[activeTab]) && (
                <div className='tarif-full-discount'>
                  {tarifsData[activeTab]?.default_discount ? (
                    <span className='tarif-default-discount'>
                      {getPrettyPrice(tarifsData[activeTab]?.default_discount)}% d'économie
                    </span>
                  ) : (
                    <></>
                  )}
                  {hasBothDiscount(tarifsData[activeTab]) && <span> + </span>}
                  {tarifsData[activeTab]?.discount?.discount_value ? (
                    <span className='tarif-network-discount'>
                      -{getPrettyPrice(tarifsData[activeTab]?.discount?.discount_value)}% (remise réseau)
                    </span>
                  ) : null}
                </div>
              )}
              <div className='tarif-price'>
                {tarifsData[activeTab]?.default_price !== tarifsData[activeTab]?.final_price && (
                  <span className='striped-tarif'>
                    {getPrettyPrice(tarifsData[activeTab]?.default_price)}
                    <span className='tarif-price-detail'>€</span>
                  </span>
                )}
                {getPrettyPrice(tarifsData[activeTab]?.final_price)}
                <span className='tarif-price-detail'>€ ht / mois</span>
              </div>
              <div className='tarif-commitment'>{tarifsData[activeTab]?.commitment}</div>
              <ul className='tarifs-advantages dots-list'>
                <li>Nombre illimité d'estimations fonds / murs / titres</li>
                <li>Estimations modifiables</li>
                <li>Rapports personnalisables (logo, carte de visite, couleurs)</li>
                <li>Dossiers vendeurs & dossiers acquéreurs</li>
                <li>25 outils pratiques à télécharger</li>
                <li>Plan de financement avec reste à vivre</li>
                <li>Simulateur de prêt</li>
                <li>Calculateur de cotisations</li>
                <li>Bibliothèque de conseils de pros</li>
                <li>Guides de terrain</li>
                <li>Mise en relation avec courtier pro</li>
                <li>Mini-formation & support utilisateur en ligne</li>
              </ul>
              <FilledButton
                onClick={() => pickTarif(tarifsData[activeTab])}
                color='var(--tarif-text-color)'
                bgColor='var(--tarif-color)'
                fontSize='1.2rem'
                disabled={isTarifDisabled(tarifsData[activeTab])}
                disabledtooltip={
                  subscription?.is_commitment
                    ? "Vous êtes déjà engagé sur un abonnement"
                    : "Vous avez déjà souscrit à cette offre"
                }
                padding='10px 25px'
                className='m-auto mt-lg'>
                Activer
              </FilledButton>
              <p className='outro-text-commitment'>
                Votre offre se renouvelle automatiquement à sa date d'échéance (mois ou année), sauf si vous choisissez
                de la résilier avant.
              </p>
            </div>
          </div>
          <Loader preventScrolling={false} visible={isLoaderVisible} className='tarifs-loader' />
        </div>
        <div className='w-100'>
          {isFreeEstimationVisible && (
            <div className='tarifs-gifted-estimation-container'>
              <h2>
                1<sup>ère</sup> estimation offerte
              </h2>
              <UnfilledButton onClick={useFreeEstimation}>J'en profite</UnfilledButton>
            </div>
          )}
          <div className='tarifs-rapports-examples'>
            <h2>Exemples de rapports</h2>
            <div className='tarifs-rapports-titles-container'>
              <div
                className={`tarifs-rapports-title ${activeRapport === 0 ? "active" : ""}`}
                onClick={() => setActiveRapport(0)}>
                Murs commerciaux
              </div>
              <div
                className={`tarifs-rapports-title ${activeRapport === 1 ? "active" : ""}`}
                onClick={() => setActiveRapport(1)}>
                Fonds de commerce
              </div>
              <div
                className={`tarifs-rapports-title ${activeRapport === 2 ? "active" : ""}`}
                onClick={() => setActiveRapport(2)}>
                Titres de société
              </div>
            </div>
            <div className='tarifs-rapport-container' onClick={() => downloadRapportExample(activeRapport)}>
              <img src={RapportMCPicture} className={`tarifs-rapport-img ${activeRapport === 0 ? "active" : ""}`} />
              <img src={RapportFDCPicture} className={`tarifs-rapport-img ${activeRapport === 1 ? "active" : ""}`} />
              <img src={RapportTDSPicture} className={`tarifs-rapport-img ${activeRapport === 2 ? "active" : ""}`} />
              <FilledButton>
                <Download width='30px' />
              </FilledButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tarifs;
